import {useContractTypes} from '@api'
import {activeCell, getUserData, isCompanyUser, isSuperAdmin, renderClickableCell, useViewNavigate} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {getUserSelectionAtom, pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface ContractTypeTableData {
  name: string
  type: string
  description: string
  active: boolean
}

export function useContractTypeSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data} = useContractTypes()

  const rows = useMemo(() => {
    if (!data) return []

    return data
      .filter(item => (isSuperAdmin() ? item.companyId === userSelection?.companyId : true))
      .map(item => ({
        name: item.name,
        type: item.type,
        description: item.note,
        active: item.active,
        id: item.id,
      }))
  }, [data])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},
        {title: t(TranslationKey['Contract type search']), link: NavigationPathKey.CONTRACT_TYPE_SEARCH},
      ]),
    [],
  )

  /**
   * Create button should only be available to Administrator users
   */
  const showCreateButton = !isCompanyUser()

  /**
   * Contract Type table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_TYPE_VIEW}/${params.row.id}`)),
    },
    {field: 'type', headerName: t(TranslationKey.Type), width: 100},
    {field: 'description', headerName: t(TranslationKey.Description), flex: 1},
    activeCell(t),
  ]

  return {navigate, t, showCreateButton, rows, columns}
}
